import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";

import { getMemberRoleLabels } from "../enums";

const DEFAULT_PER_PAGE = 15;

const INITIAL_FILTERS = {
  roles: [],
  plan_ids: [],
  subscription_statuses: [],
};

export default function useMembersList({ communityId, open }) {
  const { t } = useTranslation("memberModals");
  const roleLabels = useMemo(() => getMemberRoleLabels(t), [t]);

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [records, setRecords] = useState(0);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [reloadToken, setReloadToken] = useState(0);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const requestIdRef = useRef(0);

  const fetchMembers = useCallback(
    async ({ search: searchArg, filters: filtersArg, page: pageArg }) => {
      if (!communityId) return;

      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const params = {
          page: pageArg,
          per_page: DEFAULT_PER_PAGE,
        };
        if (searchArg && searchArg.trim().length >= 2) {
          params.search = searchArg.trim();
        }
        if (filtersArg.roles.length) params.roles = filtersArg.roles;
        if (filtersArg.plan_ids.length) params.plan_ids = filtersArg.plan_ids;
        if (filtersArg.subscription_statuses.length) {
          params.subscription_statuses = filtersArg.subscription_statuses;
        }

        const { data } = await api.get(
          `/community/${communityId}/members/list`,
          params
        );

        if (requestId !== requestIdRef.current) return;

        const list = Array.isArray(data?.data) ? data.data : [];
        setMembers(list);
        setRecords(data?.total ?? list.length);
        setPages(data?.last_page ?? 1);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load community members", error);
        setMembers([]);
        setRecords(0);
        setPages(0);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [communityId]
  );

  const fetchPlans = useCallback(async () => {
    if (!communityId) return;
    setPlansLoading(true);
    try {
      const { data } = await api.get(`/community/${communityId}/plan`);
      setPlans(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load community plans", error);
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setFilters(INITIAL_FILTERS);
    setPage(1);
    fetchPlans();
  }, [open, communityId, fetchPlans]);

  useEffect(() => {
    if (!open) return;
    fetchMembers({ search, filters, page });
  }, [open, search, filters, page, reloadToken, fetchMembers]);

  const handleSearch = useCallback((value) => {
    setSearch((prev) => {
      if (prev === value) return prev;
      setPage(1);
      return value;
    });
  }, []);

  const toggleFilterValue = useCallback((filterKey, value) => {
    setFilters((prev) => {
      const current = new Set(prev[filterKey]);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      return { ...prev, [filterKey]: Array.from(current) };
    });
    setPage(1);
  }, []);

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  }, []);

  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const changeRole = useCallback(
    (member, newRole) => {
      if (!member?.id || !communityId) return;
      if (member.role === newRole) return;

      const fromLabel = roleLabels[member.role] || member.role;
      const toLabel = roleLabels[newRole] || newRole;

      window.openConfirm?.({
        title: t("membersList.changeRole.title"),
        headline: t("membersList.changeRole.headline"),
        subtitle: t("membersList.changeRole.subtitle", {
          from: fromLabel,
          to: toLabel,
        }),
        buttons: [
          {
            text: t("common:actions.cancel"),
            variant: "text",
            onClick: () => {},
          },
          {
            text: t("membersList.changeRole.confirm"),
            variant: "contained",
            color: "primary",
            onClick: async () => {
              try {
                await api.patch(`community/member/${member.id}/permission`, {
                  community_id: communityId,
                  role: newRole,
                });
                window.setSnackbar?.(t("membersList.changeRole.success"));
                refresh();
              } catch (error) {
                console.error("Failed to change member role", error);
                window.setSnackbar?.(t("membersList.changeRole.error"));
              }
            },
          },
        ],
      });
    },
    [communityId, refresh, roleLabels, t]
  );

  const removeMember = useCallback(
    (member) => {
      if (!member?.id || !communityId) return;

      const displayName =
        member.username ||
        member.nickname ||
        t("membersList.removeMember.fallbackName");

      window.openConfirm?.({
        title: t("membersList.removeMember.title"),
        headline: t("membersList.removeMember.headline", {
          name: displayName,
        }),
        subtitle: t("membersList.removeMember.subtitle"),
        variant: "danger",
        buttons: [
          {
            text: t("common:actions.cancel"),
            variant: "text",
            onClick: () => {},
          },
          {
            text: t("membersList.removeMember.confirm"),
            variant: "contained",
            color: "error",
            onClick: async () => {
              try {
                await api.delete(`community/member/${member.id}`);
                window.setSnackbar?.(t("membersList.removeMember.success"));
                refresh();
              } catch (error) {
                console.error("Failed to remove member", error);
                window.setSnackbar?.(t("membersList.removeMember.error"));
              }
            },
          },
        ],
      });
    },
    [communityId, refresh, t]
  );

  return {
    loading,
    members,
    page,
    pages,
    records,
    search,
    filters,
    plans,
    plansLoading,
    handleSearch,
    toggleFilterValue,
    handlePageChange,
    clearFilters,
    refresh,
    changeRole,
    removeMember,
  };
}
